import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdateChannelDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-channel.form-default-values';
import { UpdateChannelModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/charge-point/update-channel.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { useCpmsBoUpdateChannelMutation } from '@service/cpms-bo-charge-point.service';

import {
  UpdateChargePointChannelContextProvider,
  useChargePointChannelContext,
  useChargePointContext,
} from '../charge-point.context';
import { ChargePointChannelUpdateFormController } from './charge-point-channel-update-form/charge-point-channel-update-form.controller';

interface ChargePointChannelUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const ChargePointChannelUpdateController = ({
  onCancel,
  onSuccess,
}: ChargePointChannelUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateChannelMutation] = useCpmsBoUpdateChannelMutation();
  const chargePoint = useChargePointContext();
  const channel = useChargePointChannelContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Pick<UpdateChannelModel, 'chargePointId' | 'channelId' | 'channel'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(
      t('$.charge-point-channel-update.error', { error: t('global.unknown') }),
    );
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Pick<UpdateChannelModel, 'chargePointId' | 'channelId' | 'channel'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.channel) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    updateChannelMutation({
      chargePointId: fieldValues.chargePointId,
      channelId: fieldValues.channelId,
      channel: sanitizedObject.channel,
    })
      .unwrap()
      .then(() => {
        toast.success(t('$.charge-point-channel-update.success'));
        onSuccess();
      })
      .catch(({ data: { message: errorMessage } }) => {
        toast.error(
          t('$.charge-point-channel-update.error', { error: errorMessage }),
        );
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER ////////////////////////////////////////////////////////////////////

  const data = getUpdateChannelDefaultValues(chargePoint.id, channel);

  return (
    <UpdateChargePointChannelContextProvider data={data}>
      <ChargePointChannelUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateChargePointChannelContextProvider>
  );
};
